import React from "react";
import { motion } from "framer-motion";
import { Brain, Shield, Stethoscope } from "lucide-react";
import { shortFaqs } from "@/data/landingFaqs";

// Paired with shortFaqs order: [Purpose, Predictions, Privacy].
// `label` is the accessible text label for the icon indicator so it never
// relies on color/shape alone.
const indicators = [
  { icon: Stethoscope, label: "Purpose" },
  { icon: Brain, label: "Predictions" },
  { icon: Shield, label: "Privacy" },
];

const ShortFAQ = React.forwardRef<HTMLElement>((_, ref) => {
  const headingId = "short-faq-heading";

  return (
    <section
      ref={ref}
      id="short-faq"
      aria-labelledby={headingId}
      className="bg-background py-20 sm:py-28"
    >
      <div className="container mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="text-center space-y-4 mb-14"
          >
            <p
              aria-hidden="true"
              className="text-xs font-medium text-primary tracking-[0.22em] uppercase"
            >
              Quick answers
            </p>
            <h2
              id={headingId}
              className="font-display text-3xl sm:text-4xl text-foreground tracking-tight"
            >
              What to know before you start.
            </h2>
          </motion.div>

          <ul
            role="list"
            className="grid sm:grid-cols-3 gap-6 sm:gap-8 list-none p-0"
          >
            {shortFaqs.map((faq, i) => {
              const indicator = indicators[i] ?? indicators[0];
              const Icon = indicator.icon;
              return (
                <motion.li
                  key={faq.q}
                  initial={{ opacity: 0, y: 6 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="rounded-2xl border border-border bg-surface p-6 sm:p-8 shadow-sm"
                >
                  <div className="mb-4 inline-flex items-center gap-2 text-primary">
                    <span
                      className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10"
                      aria-hidden="true"
                    >
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span className="sr-only">{indicator.label}:</span>
                  </div>
                  <h3 className="font-display text-lg text-foreground mb-2">
                    {faq.q}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {faq.a}
                  </p>
                </motion.li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
});

ShortFAQ.displayName = "ShortFAQ";

export default ShortFAQ;
